import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RuleBuilder, parseRule } from "@/components/RuleBuilder";
import { ConvertToDynamicDialog } from "@/components/ConvertToDynamicDialog";
import { Switch } from "@/components/ui/switch";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { Shield, ShieldAlert, Mail, KeySquare, HelpCircle, Cloud, Boxes, RefreshCw, Search, LayoutGrid, List, Eye, EyeOff, Loader2, Plus, Pencil, Trash2, Users2, X, Sparkles, ArrowRightLeft, AlertTriangle } from "lucide-react";
import { useAddressCheck } from "@/lib/useAddressCheck";

const META = {
  onprem_security: { channel: "On-Prem Agent", icon: Shield, cls: "bg-primary/10 text-primary" },
  cloud_security: { channel: "MS Graph", icon: Cloud, cls: "bg-sky-100 text-sky-700" },
  mail_onprem_security: { channel: "On-Prem Agent", icon: ShieldAlert, cls: "bg-indigo-100 text-indigo-700" },
  mail_cloud_security: { channel: "MS Graph", icon: ShieldAlert, cls: "bg-violet-100 text-violet-700" },
  cloud_distribution: { channel: "MS Graph", icon: Mail, cls: "bg-emerald-100 text-emerald-700" },
  m365_group: { channel: "MS Graph", icon: Boxes, cls: "bg-rose-100 text-rose-700" },
  licensing: { channel: "MS Graph", icon: KeySquare, cls: "bg-amber-100 text-amber-700" },
  other: { channel: "On-Prem Agent", icon: HelpCircle, cls: "bg-zinc-100 text-zinc-500" },
};
// cloud_teams managed on Teams page; cloud_distribution managed on Distribution Lists page.
const FILTER_OPTIONS = ["all", "onprem_security", "cloud_security", "mail_onprem_security", "mail_cloud_security", "licensing", "other"];
const SG_CLASSES = new Set(["onprem_security", "cloud_security", "mail_onprem_security", "mail_cloud_security"]);
const isOnprem = (g) => (g.classification || "").includes("onprem") || g.onPremisesSyncEnabled;

// --- reusable people picker (search + add) ---
function PeoplePicker({ users, exclude, onPick, placeholder, testid }) {
  const [q, setQ] = useState("");
  const opts = useMemo(() => {
    const ex = new Set(exclude || []);
    const s = q.toLowerCase();
    return users.filter((u) => !ex.has(u.id) &&
      `${u.displayName} ${u.userPrincipalName || u.mail || ""}`.toLowerCase().includes(s)).slice(0, 6);
  }, [users, q, exclude]);
  return (
    <div className="relative">
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="rounded-sm border-zinc-300 pl-8 text-sm" />
      {q && opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full border border-zinc-200 bg-white shadow-lg">
          {opts.map((u) => (
            <button key={u.id} type="button" onClick={() => { onPick(u); setQ(""); }} data-testid={`sg-pick-${u.id}`}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-zinc-50">
              <span className="font-medium text-zinc-800">{u.displayName}</span>
              <span className="text-xs text-zinc-400">{u.userPrincipalName || u.mail}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- domain picker (local part + @domain dropdown) ---
function DomainSelect({ value, onChange, testid, options, defaultDomainId }) {
  if (!options.length) {
    return <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="domain.com" data-testid={testid} className="w-44 shrink-0 rounded-sm border-zinc-300 font-mono text-sm" />;
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger title={value} className="w-72 shrink-0 rounded-sm border-zinc-300 font-mono text-xs" data-testid={testid}><SelectValue placeholder="@domain" /></SelectTrigger>
      <SelectContent>{options.map((d) => <SelectItem key={d} value={d}>@{d}{d === defaultDomainId ? " (default)" : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Groups() {
  const { isAdmin, can } = useAuth();
  const canManage = can("groups.manage");
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [visFilter, setVisFilter] = useState("visible");
  const sort = useTableSort("displayName", "asc", "groups");
  const cols = useColumns("groups");
  const [view, setView] = useState("list");
  const [form, setForm] = useState(null);      // {mode, group}
  const [del, setDel] = useState(null);
  const [manage, setManage] = useState(null);
  const [convert, setConvert] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/groups").then((r) => setGroups(r.data.filter((g) => !["cloud_teams", "cloud_distribution", "licensing", "m365_group"].includes(g.classification) && !g.applicationId && !(g.assignedLicenses && g.assignedLicenses.length))));
    if (isAdmin) api.get("/settings").then((r) => setLastSync(r.data.groupSync?.lastSync)).catch(() => {});
  };
  useEffect(() => {
    load();
    if (canManage) api.get("/users").then((r) => setUsers((r.data || []).filter((u) => !u.isShared))).catch(() => {});
  }, [isAdmin, canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/groups/sync"); toast.success(`Synced ${data.synced} groups`); setLastSync(data.lastSync); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSyncing(false); }
  };
  const clsLabel = (c) => t(`groups.cls.${c}`);
  const toggleVis = async (gid, isVisible) => {
    setGroups((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible } : x));
    try { await api.patch(`/groups/${gid}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setGroups((gs) => gs.map((x) => x.id === gid ? { ...x, isVisible: !isVisible } : x)); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/security-groups/${del.id}`); toast.success(t("sg.deleted")); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const VisToggle = ({ g }) => {
    const hidden = g.isVisible === false;
    return (
      <button onClick={() => toggleVis(g.id, hidden)} data-testid={`group-vis-${g.id}`}
        title={hidden ? t("groups.toggleHidden") : t("groups.toggleVisible")}
        className={`flex h-8 w-8 items-center justify-center border transition-colors ${hidden ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
        {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    );
  };

  const RowActions = ({ g }) => {
    if (!canManage || !SG_CLASSES.has(g.classification)) return null;
    const canConvert = g.classification === "cloud_security" && !g.dynamicMembership && !g.mailEnabled;
    return (
      <div className="flex items-center gap-1">
        <button onClick={() => setManage(g)} data-testid={`sg-members-${g.id}`} title={t("sg.members")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-primary"><Users2 size={14} /></button>
        {canConvert && <button onClick={() => setConvert(g)} data-testid={`sg-convert-${g.id}`} title={t("convert.action")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-violet-50 hover:text-violet-600"><ArrowRightLeft size={14} /></button>}
        <button onClick={() => setForm({ mode: "edit", group: g })} data-testid={`sg-edit-${g.id}`} title={t("sg.edit")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-primary"><Pencil size={14} /></button>
        <button onClick={() => setDel(g)} data-testid={`sg-delete-${g.id}`} title={t("sg.delete")} className="flex h-8 w-8 items-center justify-center border border-zinc-200 text-zinc-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
      </div>
    );
  };

  const GroupCard = ({ g }) => {
    const m = META[g.classification] || META.other;
    return (
      <div className="flex flex-col bg-white p-5" data-testid={`group-card-${g.id}`}>
        <div className="flex items-start justify-between">
          <div className={`flex h-9 w-9 items-center justify-center ${m.cls}`}><m.icon size={18} /></div>
          <span className="font-display text-2xl font-black tracking-tighter text-zinc-950">{g.memberCount ?? 0}</span>
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm font-bold text-zinc-900">{g.displayName}{g.dynamicMembership && <span data-testid={`group-dynamic-${g.id}`} className="border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("groups.dynamic")}</span>}</div>
        <div className="mt-0.5 flex-1 text-xs text-zinc-500">{g.description}</div>
        <div className="mt-3 flex items-center justify-between">
          <span className="border border-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-600">{clsLabel(g.classification)}</span>
          <span className="font-mono text-[10px] text-zinc-400">→ {m.channel}</span>
        </div>
        {(isAdmin || canManage) && <div className="mt-3 flex items-center gap-2 border-t border-zinc-100 pt-3">{isAdmin && <VisToggle g={g} />}<RowActions g={g} /></div>}
      </div>
    );
  };

  const rows = useMemo(() => {
    const r = groups.filter((g) => (filter === "all" || g.classification === filter) &&
      (visFilter === "all" || (visFilter === "visible" ? g.isVisible !== false : g.isVisible === false)) &&
      `${g.displayName} ${g.description || ""}`.toLowerCase().includes(q.toLowerCase()));
    return sort.sortRows(r, {
      type: (g) => g.classification || "",
      channel: (g) => (META[g.classification] || META.other).channel,
      members: (g) => g.memberCount ?? 0,
    });
  }, [groups, q, filter, visFilter, sort]);
  const { visible, sentinelRef, hasMore, shown, total } = useInfiniteList(rows, 50);
  const csvHeaders = [
    { key: "displayName", label: t("groups.colName") },
    ...(cols.show("type") ? [{ key: "type", label: t("groups.colType") }] : []),
    ...(cols.show("channel") ? [{ key: "channel", label: t("groups.colChannel") }] : []),
    { key: "members", label: t("groups.colMembers") },
  ];
  const csvRows = rows.map((g) => ({ displayName: g.displayName, type: clsLabel(g.classification), channel: (META[g.classification] || META.other).channel, members: g.memberCount ?? 0 }));

  return (
    <>
      <PageHeader title={t("groups.title")} subtitle={`${rows.length} ${t("groups.title").toLowerCase()}`}>
        <div className="flex items-center gap-3">
          {isAdmin && <span className="hidden font-mono text-[11px] text-zinc-400 sm:inline">{t("groups.lastSync")}: {lastSync ? new Date(lastSync).toLocaleString() : "—"}</span>}
          {isAdmin && (
            <Button onClick={sync} disabled={syncing} variant="outline" data-testid="groups-sync-now" className="rounded-sm">
              <RefreshCw size={15} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> {t("groups.syncNow")}
            </Button>
          )}
          {canManage && (
            <Button onClick={() => setForm({ mode: "create", group: {} })} data-testid="sg-create-button" className="rounded-sm bg-primary hover:bg-[#002480]">
              <Plus size={16} className="mr-2" /> {t("sg.create")}
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="p-6 md:p-10">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("groups.searchPh")} data-testid="groups-search" className="rounded-sm border-zinc-300 pl-9" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "type", label: t("groups.colType") }, { key: "channel", label: t("groups.colChannel") }]} />
            <ExportCsvButton label={t("cols.export")} filename="security-groups.csv" headers={csvHeaders} rows={csvRows} />
          </div>
          <div className="flex border border-zinc-200">
            <button onClick={() => setView("list")} data-testid="groups-view-list" className={`p-2 ${view === "list" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}><List size={16} /></button>
            <button onClick={() => setView("card")} data-testid="groups-view-card" className={`p-2 ${view === "card" ? "bg-zinc-900 text-white" : "bg-white text-zinc-500"}`}><LayoutGrid size={16} /></button>
          </div>
        </div>

        {view === "list" ? (
          <>
          <div className="hidden border border-zinc-200 bg-white md:block">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  <SortTh label={t("groups.colName")} sortKey="displayName" sort={sort} />
                  {cols.show("type") && <SortTh label={t("groups.colType")} sortKey="type" sort={sort} className="hidden md:table-cell" filter={{ value: filter, onChange: setFilter, allValue: "all", options: FILTER_OPTIONS.map((o) => ({ value: o, label: o === "all" ? t("common.all") : clsLabel(o) })) }} />}
                  {cols.show("channel") && <SortTh label={t("groups.colChannel")} sortKey="channel" sort={sort} className="hidden lg:table-cell" />}
                  <SortTh label={t("groups.colMembers")} sortKey="members" sort={sort} />
                  {isAdmin && <SortTh label={t("groups.colVisible")} sortKey="isVisible" sort={sort} filter={{ value: visFilter, onChange: setVisFilter, allValue: "all", options: [
                    { value: "visible", label: t("groups.visVisible") },
                    { value: "hidden", label: t("groups.visHidden") },
                    { value: "all", label: t("groups.visAll") },
                  ] }} />}
                  {canManage && <th className="px-4 py-3 text-right">{t("m365.colActions")}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((g) => {
                  const m = META[g.classification] || META.other;
                  return (
                    <tr key={g.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`group-row-${g.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className={`flex h-8 w-8 items-center justify-center ${m.cls}`}><m.icon size={16} /></span>
                          <div><div className="flex items-center gap-2 text-sm font-semibold text-zinc-900">{g.displayName}{g.dynamicMembership && <span data-testid={`group-dynamic-${g.id}`} className="border border-violet-200 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("groups.dynamic")}</span>}</div><div className="text-xs text-zinc-400">{g.description}</div></div>
                        </div>
                      </td>
                      {cols.show("type") && <td className="hidden px-4 py-3 md:table-cell"><span className="border border-zinc-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-600">{clsLabel(g.classification)}</span></td>}
                      {cols.show("channel") && <td className="hidden px-4 py-3 font-mono text-xs text-zinc-500 lg:table-cell">{m.channel}</td>}
                      <td className="px-4 py-3 font-display text-lg font-black text-zinc-900">{g.memberCount ?? 0}</td>
                      {isAdmin && <td className="px-4 py-3"><VisToggle g={g} /></td>}
                      {canManage && <td className="px-4 py-3"><div className="flex justify-end"><RowActions g={g} /></div></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile card fallback for the list view */}
          <div className="grid grid-cols-1 gap-px bg-zinc-200 md:hidden" data-testid="groups-mobile-list">
            {visible.map((g) => <GroupCard key={g.id} g={g} />)}
          </div>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((g) => <GroupCard key={g.id} g={g} />)}
          </div>
        )}
        {hasMore && <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-zinc-400"><Loader2 size={14} className="mr-2 animate-spin" /> {shown}/{total}</div>}
      </div>

      {form && <SGFormDialog form={form} users={users} onClose={() => setForm(null)} onSaved={load} t={t} />}
      {manage && <SGMembersDialog group={manage} users={users} onClose={() => setManage(null)} onChanged={load} t={t} />}
      {convert && <ConvertToDynamicDialog group={convert} apiBase="/security-groups" onClose={() => setConvert(null)} onDone={() => { setConvert(null); load(); }} />}

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="rounded-sm" data-testid="sg-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{t("sg.deleteConfirm")}</DialogTitle>
            <DialogDescription className="text-xs">{t("sg.deleteWarn")} <strong>{del?.displayName}</strong>{del && isOnprem(del) && <><br /><span className="mt-2 block text-zinc-400">{t("sg.deleteOnpremNote")}</span></>}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={doDelete} disabled={busy} data-testid="sg-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("sg.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// --- create / edit dialog ---
function SGFormDialog({ form, users, onClose, onSaved, t }) {
  const isEdit = form.mode === "edit";
  const g = form.group || {};
  const editOnprem = isEdit && isOnprem(g);
  const [displayName, setName] = useState(g.displayName || "");
  const [description, setDesc] = useState(g.description || "");
  const [source, setSource] = useState(editOnprem ? "onprem" : "cloud");
  const [mailEnabled, setMailEnabled] = useState(g.mailEnabled || false);
  const split = (a) => { const i = (a || "").indexOf("@"); return i < 0 ? { local: a || "", domain: "" } : { local: a.slice(0, i), domain: a.slice(i + 1) }; };
  const [primaryLocal, setPrimaryLocal] = useState(split(g.mail || "").local);
  const [primaryDomain, setPrimaryDomain] = useState(split(g.mail || "").domain);
  const [aliasRows, setAliasRows] = useState((g.aliases || []).map(split));
  const [hiddenFromGal, setHiddenGal] = useState(!!g.hiddenFromGal);
  const [domains, setDomains] = useState([]);
  const [membershipType, setMType] = useState(g.dynamicMembership ? "dynamic" : "assigned");
  const [membershipRule, setRule] = useState(g.membershipRule || "");
  const [owners, setOwners] = useState([]);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const uMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const isOnpremSrc = source === "onprem";
  const isDynamic = !isOnpremSrc && !mailEnabled && membershipType === "dynamic";

  useEffect(() => {
    api.get("/domains").then((r) => {
      const doms = r.data || [];
      setDomains(doms);
      const def = (doms.find((d) => d.isDefault) || doms[0] || {}).id || "";
      if (def) setPrimaryDomain((p) => p || def);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const defaultDomainId = (domains.find((d) => d.isDefault) || {}).id || "";
  const domainOptions = useMemo(() => {
    const ids = [];
    domains.forEach((d) => ids.push(d.id));
    [primaryDomain, ...aliasRows.map((a) => a.domain)].forEach((d) => { if (d && !ids.includes(d)) ids.push(d); });
    return ids;
  }, [domains, primaryDomain, aliasRows]);
  const setAlias = (i, patch) => setAliasRows((rows) => rows.map((r, x) => (x === i ? { ...r, ...patch } : r)));
  const addAlias = () => setAliasRows((rows) => [...rows, { local: "", domain: defaultDomainId || domainOptions[0] || "" }]);
  const removeAlias = (i) => setAliasRows((rows) => rows.filter((_, x) => x !== i));

  const composeAddr = (loc, dom) => (loc && loc.trim() && dom ? `${loc.trim()}@${dom}` : "");
  const primaryAddr = composeAddr(primaryLocal, primaryDomain);
  const aliasAddrs = aliasRows.map((a) => composeAddr(a.local, a.domain));
  const { conflicts } = useAddressCheck(mailEnabled && !isEdit ? [primaryAddr, ...aliasAddrs] : [], null);
  const ConflictHint = ({ addr }) => {
    const c = addr && conflicts[addr];
    if (!c) return null;
    return <p className="mt-1 flex items-center gap-1 text-xs font-medium text-amber-700" data-testid={`sg-addr-conflict-${addr}`}><AlertTriangle size={12} /> {t("addr.inUse")} {t(`addr.type.${c.type}`)} "{c.displayName}"</p>;
  };

  const submit = async () => {
    if (!displayName.trim()) { toast.error(t("sg.needName")); return; }
    const composedEmail = primaryAddr;
    if (!isEdit && mailEnabled && (!primaryLocal.trim() || !primaryDomain)) { toast.error(t("sg.email")); return; }
    if (isDynamic && parseRule(membershipRule).clauses.length === 0) { toast.error(t("sg.needRule")); return; }
    const aliases = aliasRows.filter((r) => r.local.trim() && r.domain).map((r) => `${r.local.trim()}@${r.domain}`);
    if (mailEnabled && !isEdit && Object.keys(conflicts).length) { toast.error(t("addr.conflictSave")); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const patch = { displayName, description };
        if (!editOnprem && !g.mailEnabled) { patch.membershipType = membershipType; patch.membershipRule = isDynamic ? membershipRule : ""; }
        await api.patch(`/security-groups/${g.id}`, patch);
        toast.success(t("sg.updated"));
      } else {
        await api.post(`/security-groups`, {
          displayName, description, source, mailEnabled, email: mailEnabled ? composedEmail : null,
          aliases: mailEnabled ? aliases : [], hiddenFromGal: mailEnabled ? hiddenFromGal : false,
          membershipType: (isOnpremSrc || mailEnabled) ? "assigned" : membershipType,
          membershipRule: isDynamic ? membershipRule : "", owners, members: isDynamic ? [] : members,
        });
        toast.success(t("sg.created"));
      }
      onClose(); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const Chip = ({ id, onRemove }) => (
    <span className="inline-flex items-center gap-1 border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs">
      {uMap[id]?.displayName || id}
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
    </span>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="sg-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{isEdit ? t("sg.editTitle") : t("sg.createTitle")}</DialogTitle>
          <DialogDescription className="text-xs">{isEdit ? t("sg.editDesc") : t("sg.createDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.fieldName")}</label>
            <Input value={displayName} onChange={(e) => setName(e.target.value)} data-testid="sg-field-name" className="rounded-sm border-zinc-300" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.fieldDescription")}</label>
            <Textarea value={description} onChange={(e) => setDesc(e.target.value)} data-testid="sg-field-desc" className="rounded-sm border-zinc-300" rows={2} />
          </div>
          {!isEdit && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.fieldSource")}</label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="sg-field-source" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cloud">{t("sg.sourceCloud")}</SelectItem>
                  <SelectItem value="onprem">{t("sg.sourceOnprem")}</SelectItem>
                </SelectContent>
              </Select>
              {isOnpremSrc && <p className="mt-1 text-[11px] text-zinc-400">{t("sg.sourceOnpremNote")}</p>}
            </div>
          )}
          {!isEdit && (
            <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5">
              <div className="pr-3">
                <div className="text-sm font-medium text-zinc-800">{t("sg.mailEnabled")}</div>
                <div className="text-xs text-zinc-400">{t("sg.mailEnabledHelp")}</div>
              </div>
              <Switch checked={mailEnabled} onCheckedChange={setMailEnabled} data-testid="sg-field-mailenabled" />
            </div>
          )}
          {!isEdit && mailEnabled && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.email")}</label>
                <div className="flex items-center gap-2">
                  <Input value={primaryLocal} onChange={(e) => setPrimaryLocal(e.target.value)} placeholder="team" data-testid="sg-field-email-local" className="rounded-sm border-zinc-300 font-mono text-sm" />
                  <span className="text-sm font-semibold text-zinc-400">@</span>
                  <DomainSelect value={primaryDomain} onChange={setPrimaryDomain} testid="sg-field-email-domain" options={domainOptions} defaultDomainId={defaultDomainId} />
                </div>
                <ConflictHint addr={primaryAddr} />
                <p className="mt-1 text-[11px] text-zinc-400">{t("sg.mailDynamicNote")}</p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("dl.fieldAliases")}</label>
                <div className="space-y-2">
                  {aliasRows.length === 0 && <p className="text-xs text-zinc-400">{t("dl.noAliases")}</p>}
                  {aliasRows.map((a, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-2">
                        <Input value={a.local} onChange={(e) => setAlias(i, { local: e.target.value })} placeholder="team-alt" data-testid={`sg-field-alias-local-${i}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
                        <span className="text-sm font-semibold text-zinc-400">@</span>
                        <DomainSelect value={a.domain} onChange={(v) => setAlias(i, { domain: v })} testid={`sg-field-alias-domain-${i}`} options={domainOptions} defaultDomainId={defaultDomainId} />
                        <button type="button" onClick={() => removeAlias(i)} data-testid={`sg-field-alias-remove-${i}`} className="p-1.5 text-zinc-400 hover:text-red-600"><X size={15} /></button>
                      </div>
                      <ConflictHint addr={aliasAddrs[i]} />
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addAlias} data-testid="sg-field-alias-add" className="rounded-sm border-zinc-300"><Plus size={14} className="mr-1.5" /> {t("dl.addAlias")}</Button>
                </div>
              </div>
              <div className="flex items-center justify-between border border-zinc-200 px-3 py-2.5">
                <div className="pr-3">
                  <div className="text-sm font-medium text-zinc-800">{t("gal.hide")}</div>
                  <div className="text-xs text-zinc-400">{t("gal.hideHelp")}</div>
                </div>
                <Switch checked={hiddenFromGal} onCheckedChange={setHiddenGal} data-testid="sg-field-hidegal" />
              </div>
            </>
          )}
          {!isOnpremSrc && !editOnprem && !mailEnabled && !g.mailEnabled && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.membershipType")}</label>
              <Select value={membershipType} onValueChange={setMType}>
                <SelectTrigger data-testid="sg-field-membershiptype" className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="assigned">{t("sg.assigned")}</SelectItem>
                  <SelectItem value="dynamic">{t("sg.dynamicType")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isDynamic && (
            <div className="border border-violet-200 bg-violet-50/40 p-3">
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-violet-700"><Sparkles size={13} /> {t("sg.dynamicNote")}</div>
              <RuleBuilder value={membershipRule} onChange={setRule} testid="sg-rule" preview templates />
            </div>
          )}
          {!isEdit && !isDynamic && (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.owners")}</label>
                <PeoplePicker users={users} exclude={owners} onPick={(u) => setOwners((o) => [...o, u.id])} placeholder={t("sg.searchPeople")} testid="sg-owner-picker" />
                <div className="mt-2 flex flex-wrap gap-1.5">{owners.map((id) => <Chip key={id} id={id} onRemove={() => setOwners((o) => o.filter((x) => x !== id))} />)}</div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-600">{t("sg.membersField")}</label>
                <PeoplePicker users={users} exclude={members} onPick={(u) => setMembers((m) => [...m, u.id])} placeholder={t("sg.searchPeople")} testid="sg-member-picker" />
                <div className="mt-2 flex flex-wrap gap-1.5">{members.map((id) => <Chip key={id} id={id} onRemove={() => setMembers((m) => m.filter((x) => x !== id))} />)}</div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="sg-form-submit" className="rounded-sm bg-primary hover:bg-[#002480]">{isEdit ? t("common.save") : t("sg.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- members management ---
function SGMembersDialog({ group, users, onClose, onChanged, t }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/security-groups/${group.id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [group.id]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); onChanged(); } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); } };
  const addMember = (u) => act(() => api.post(`/security-groups/${group.id}/members`, { userId: u.id }));
  const removeMember = (id) => act(() => api.delete(`/security-groups/${group.id}/members/${id}`));

  const owners = detail?.owners || [];
  const members = detail?.members || [];
  const isDynamic = detail?.dynamic === true;
  const onprem = detail?.onPrem === true;

  const Row = ({ p, onRemove, canRemove, testid }) => (
    <div className="flex items-center justify-between border-b border-zinc-100 py-2 last:border-0">
      <div><div className="text-sm font-medium text-zinc-800">{p.displayName}</div><div className="text-xs text-zinc-400">{p.userPrincipalName || p.mail}</div></div>
      {onRemove && <button onClick={onRemove} disabled={busy || !canRemove} data-testid={testid}
        className="flex h-7 w-7 items-center justify-center border border-zinc-200 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"><X size={13} /></button>}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="sg-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">{t("sg.membersTitle")} — {group.displayName}
            {onprem && <span className="border border-zinc-200 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500">{t("sg.onpremBadge")}</span>}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("sg.ownersList")} ({owners.length})</div>
            <div className="mt-1">{owners.length === 0 ? <p className="text-xs text-zinc-400">{t("sg.noOwners")}</p> :
              owners.map((p) => <Row key={p.id} p={p} testid={`sg-owner-${p.id}`} />)}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("sg.membersList")} ({members.length})</div>
            {isDynamic ? (
              <p className="mb-2 border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-700" data-testid="sg-dynamic-members-note">{t("sg.dynamicMembersNote")}</p>
            ) : (
              <PeoplePicker users={users} exclude={members.map((m) => m.id)} onPick={addMember} placeholder={t("sg.addMember")} testid="sg-add-member" />
            )}
            <div className="mt-2">{members.length === 0 ? <p className="text-xs text-zinc-400">{t("sg.noMembers")}</p> :
              members.map((p) => <Row key={p.id} p={p} onRemove={isDynamic ? undefined : () => removeMember(p.id)} canRemove={!isDynamic} testid={`sg-remove-member-${p.id}`} />)}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
